import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const AuthContext = createContext(null);
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

let refreshRequest = null;
let lastPermissionToastAt = 0;
// Set by AuthProvider: drops the signed-in user so routes go back to /login
// when the session can no longer be refreshed (expired, revoked, logged out elsewhere).
let onSessionExpired = null;

// A warehouse-role user still sees write buttons on a few pages we haven't
// hidden yet; their own catch block then shows a generic "Failed to ..."
// toast right after this interceptor's specific permission message. Swallow
// that immediate duplicate so the user only sees one, more useful, toast.
const originalToastError = toast.error.bind(toast);
toast.error = (message, options) => {
  if (Date.now() - lastPermissionToastAt < 600) return;
  return originalToastError(message, options);
};

axios.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;
    if (error.response?.status === 403 && typeof error.response?.data?.detail === 'string'
        && error.response.data.detail.includes('edit access to Purchases')) {
      lastPermissionToastAt = Date.now();
      originalToastError(error.response.data.detail);
      return Promise.reject(error);
    }
    if (
      error.response?.status !== 401 ||
      !originalRequest ||
      originalRequest._retry ||
      originalRequest._skipAuthRefresh
    ) {
      return Promise.reject(error);
    }
    originalRequest._retry = true;
    try {
      if (!refreshRequest) {
        refreshRequest = axios.post(
          `${API}/auth/refresh`,
          {},
          { withCredentials: true, _skipAuthRefresh: true }
        ).finally(() => { refreshRequest = null; });
      }
      await refreshRequest;
      return axios(originalRequest);
    } catch {
      if (onSessionExpired) onSessionExpired();
      return Promise.reject(error);
    }
  }
);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// The last signed-in user (email, name, role, countries: what /auth/me returns, nothing secret) so the app can draw
// at once on the next visit instead of a "Loading..." screen. It only decides what the UI shows: every API call is
// still checked by the server, and a session that turns out to be gone signs the user out as before.
const USER_KEY = 'giq.session.user';
const CACHE_DAYS = 7;   // the refresh-token lifetime (JWT_REFRESH_DAYS): an older copy cannot be a live session
const readCachedUser = () => {
  try {
    const raw = window.localStorage.getItem(USER_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    const fresh = parsed && Date.now() - Number(parsed.saved_at || 0) < CACHE_DAYS * 864e5;
    return fresh && typeof parsed.email === 'string' && typeof parsed.role === 'string' ? parsed : null;
  } catch {
    return null;
  }
};
const writeCachedUser = (value) => {
  try {
    if (value) {
      window.localStorage.setItem(USER_KEY, JSON.stringify({ email: value.email, name: value.name, role: value.role,
        countries: value.countries, saved_at: Date.now() }));
    } else window.localStorage.removeItem(USER_KEY);
  } catch {
    /* storage unavailable: the app still works, it just shows Loading... on the next visit */
  }
};

export const AuthProvider = ({ children }) => {
  const [cached] = useState(readCachedUser);
  const [user, setUserState] = useState(cached);
  const [loading, setLoading] = useState(!cached);
  // `verified`: the server has confirmed the session in this tab. Admin-only pages wait for it (a cached role
  // could be out of date); everything else draws straight away from the remembered user.
  const [verified, setVerified] = useState(false);
  const setUser = (value) => { writeCachedUser(value); setUserState(value); };

  useEffect(() => {
    // Signed out (or in as someone else) in another tab: follow it here.
    const onStorage = (event) => {
      if (event.key !== USER_KEY) return;
      setUserState(readCachedUser());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  useEffect(() => {
    onSessionExpired = () => setUser(null);
    return () => { onSessionExpired = null; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Confirm the session in the background (after a refresh if the access token expired).
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then(response => setUser(response.data))
      .catch((error) => {
        // Signed out only when the server says so; a network blip keeps the remembered user (pages show their own errors).
        if (!cached || error?.response?.status === 401 || error?.response?.status === 403) setUser(null);
      })
      .finally(() => { setLoading(false); setVerified(true); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = async (email, password) => {
    const response = await axios.post(
      `${API}/auth/login`,
      { email, password },
      { withCredentials: true, _skipAuthRefresh: true }
    );
    const { user: userData } = response.data;
    setUser(userData);
    setVerified(true);
    return userData;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true, _skipAuthRefresh: true });
    } catch {
      // The local copy goes anyway, but the server could not end the session: say so instead of failing silently.
      toast.error('Could not reach the server to sign out. Close the browser, or sign out again when you are back online.');
    } finally {
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, verified }}>
      {children}
    </AuthContext.Provider>
  );
};

export const getAuthHeaders = () => {
  return {};
};